import type { IWalletAddress, IWalletInfo, IWalletInfoFlags } from '../types';

const APPLE_BILLING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress', 'name', 'phoneticName'];
const APPLE_SHIPPING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress', 'name', 'phoneticName', 'phone', 'email'];
// Apple returns the email on the shipping contact only
const APPLE_EMAIL_ONLY_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['email'];

// A value the wallet does not provide is null, an empty string is returned as is
const toNullable = <T>(value: T | null | undefined): T | null => value ?? null;

// null when the wallet provides none of the parts, otherwise the non-empty parts divided with a space
const joinParts = (...parts: Array<string | undefined>): string | null =>
  parts.every((part) => part === undefined || part === null)
    ? null
    : parts.filter(Boolean).join(' ');

export const getAppleWalletInfoContactFields = ({ isBilling, isShipping }: IWalletInfoFlags): {
  requiredBillingContactFields: ApplePayJS.ApplePayContactField[];
  requiredShippingContactFields: ApplePayJS.ApplePayContactField[];
} => ({
  requiredBillingContactFields: isBilling ? APPLE_BILLING_CONTACT_FIELDS : [],
  requiredShippingContactFields: isShipping
    ? APPLE_SHIPPING_CONTACT_FIELDS
    : isBilling ? APPLE_EMAIL_ONLY_CONTACT_FIELDS : [],
});

export const getGoogleWalletInfoRequest = ({ isBilling, isShipping }: IWalletInfoFlags): Pick<
  google.payments.api.PaymentDataRequest,
  'emailRequired' | 'shippingAddressRequired' | 'shippingAddressParameters'
> & Pick<google.payments.api.CardParameters, 'billingAddressRequired' | 'billingAddressParameters'> => ({
  emailRequired: isBilling || isShipping,
  shippingAddressRequired: isShipping,
  ...(isShipping && { shippingAddressParameters: { phoneNumberRequired: true } }),
  billingAddressRequired: isBilling,
  ...(isBilling && { billingAddressParameters: { format: 'FULL', phoneNumberRequired: true } }),
});

const mapAppleContact = (contact: ApplePayJS.ApplePayPaymentContact | undefined): IWalletAddress | null => {
  if (!contact) return null;

  return {
    fullName: joinParts(contact.givenName, contact.familyName),
    phoneticFullName: joinParts(contact.phoneticGivenName, contact.phoneticFamilyName),
    phoneNumber: toNullable(contact.phoneNumber),
    addressLines: toNullable(contact.addressLines),
    locality: toNullable(contact.locality),
    subLocality: toNullable(contact.subLocality),
    administrativeArea: toNullable(contact.administrativeArea),
    subAdministrativeArea: toNullable(contact.subAdministrativeArea),
    postalCode: toNullable(contact.postalCode),
    country: toNullable(contact.country),
    countryCode: toNullable(contact.countryCode),
    // Apple only
    sortingCode: null,
  };
};

const mapGoogleAddress = (address: google.payments.api.Address | undefined): IWalletAddress | null => {
  if (!address) return null;

  const lines = [address.address1, address.address2, address.address3];

  return {
    fullName: toNullable(address.name),
    // Google only
    phoneticFullName: null,
    phoneNumber: toNullable(address.phoneNumber),
    // Empty Google lines are left out
    addressLines: lines.every((line) => line === undefined || line === null)
      ? null
      : lines.filter((line): line is string => !!line),
    locality: toNullable(address.locality),
    subLocality: null,
    administrativeArea: toNullable(address.administrativeArea),
    subAdministrativeArea: null,
    postalCode: toNullable(address.postalCode),
    country: null,
    countryCode: toNullable(address.countryCode),
    sortingCode: toNullable(address.sortingCode),
  };
};

export const getAppleWalletInfo = (
  payment: ApplePayJS.ApplePayPayment,
  { isBilling, isShipping }: IWalletInfoFlags,
): IWalletInfo | undefined => {
  if (!isBilling && !isShipping) return undefined;

  return {
    email: toNullable(payment.shippingContact?.emailAddress),
    ...(isBilling && { billing: mapAppleContact(payment.billingContact) }),
    ...(isShipping && { shipping: mapAppleContact(payment.shippingContact) }),
  };
};

export const getGoogleWalletInfo = (
  paymentData: google.payments.api.PaymentData,
  { isBilling, isShipping }: IWalletInfoFlags,
): IWalletInfo | undefined => {
  if (!isBilling && !isShipping) return undefined;

  return {
    email: toNullable(paymentData.email),
    ...(isBilling && { billing: mapGoogleAddress(paymentData.paymentMethodData.info?.billingAddress) }),
    ...(isShipping && { shipping: mapGoogleAddress(paymentData.shippingAddress) }),
  };
};
